import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import {
  ConfirmContent,
  ItemWithMetaData,
  ItemsForReviewResponse,
} from "../api/Items";
import { useCreateItemPostRating } from "../api/ItemsApi";
import {
  useCreateProfileInProgress,
  useRemoveProfileAndPosts,
} from "../api/ProfilesApi";
import {
  ACTOR_TIMER_STATUS_UI_ENUM,
  ReportSummaryItemApi,
} from "../api/Reports";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useProfileManualRateDone } from "../api/WorkflowApi";
import Button from "../shared-components/Button";
import { ManualConfirmedProfiles } from "./ManualConfirmedProfiles";
import SuggestedProfiles from "./SuggestedProfiles";
import {
  useReportDetail,
  useReportTimer,
} from "../reports/ReportDetailProvider";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { useConfirmationDialog } from "../shared-components/ConfirmationDialog";
import ConfirmedProfiles from "./ConfirmedProfiles";
import ProfilerProfileConfirm from "./ProfilerProfileConfirm";
import { useFamaFlags } from "../api/FamaFlags";
import { sortProfilesByPlatform } from "./util";
import { useWorkflowStepActorTimer } from "../reports/useWorkflowStepActorTimer";
import { WORKFLOW_STEP_STATUS_ENUM } from "../api/Workflows";
import { useProfilerProfileOperations } from "./useProfilerProfileOperations";
import { useDeferredProfileRemove } from "./useDeferredProfileRemove";
import { ProfileAddConfirmation } from "./profileConfirmEntities";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import { profileUrlsMatch } from "../utils/personFieldLimits";

function urlInExistingProfiles(url: string, profileList: ItemWithMetaData[]) {
  return profileList.some((profile) =>
    profileUrlsMatch(profile.item.content?.url ?? "", url)
  );
}

const ProfileConfirm = ({
  isReadOnly,
  handleExit,
  report,
  isAmendment,
}: {
  isReadOnly: boolean;
  handleExit: () => void;
  report: ReportSummaryItemApi;
  isAmendment: boolean;
}) => {
  const navigate = useNavigate();
  const { canConfirmProfile } = usePermissionValidator();
  const { enableProfiler } = useFamaFlags();
  const { resetTimer } = useReportTimer();
  const {
    loadProfilesConfirm,
    loadMoreProfilesConfirm,
    hasMoreProfilesConfirm,
    isLoadingMoreProfilesConfirm,
    profileListConfirm,
    isSuccessProfilesConfirm,
    isLoadingProfilesConfirm,
  } = useReportDetail();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { showPushToast } = useApplicationToast();
  const { modal, show } = useConfirmationDialog();
  const {
    isLoading: isLoadingProfileManualRateDone,
    error: errorProfileManualRate,
    mutate: profileManualRateDone,
    isSuccess: successProfileManualRateDone,
    variables: manualRateVariables,
  } = useProfileManualRateDone();

  const {
    mutate: createItemRating,
    isLoading: isLoadingItemRating,
    reset: resetCreate,
    isSuccess: successRating,
  } = useCreateItemPostRating();

  const {
    isLoading: isLoadingRemoveProfile,
    mutate: removeProfile,
    isSuccess: successRemoveProfile,
    reset: resetRemove,
    error: errorRemoveProfile,
  } = useRemoveProfileAndPosts();

  const {
    mutate: createProfile,
    isSuccess: successCreateProfile,
    isLoading: isLoadingCreateProfile,
    error: errorCreateProfile,
  } = useCreateProfileInProgress();

  const handleProfileAdded = useCallback(() => {
    showPushToast?.({
      message: "Profile has been added",
      type: "success-white-bg",
    });
  }, [showPushToast]);

  // Profiler errors use the banner only (not a push toast). Undo remove toasts
  // are sticky, but keeping errors on the banner avoids competing notifications.
  const profilerOperations = useProfilerProfileOperations({
    loadProfilesConfirm,
    onError: setErrorMessage,
    onSuccess: () => setErrorMessage(""),
    onProfileAdded: handleProfileAdded,
  });

  const cachedProfileListConfirmRef = useRef<
    ItemsForReviewResponse | undefined
  >();
  if (profileListConfirm) {
    cachedProfileListConfirmRef.current = profileListConfirm;
  }
  const stableProfileListConfirm =
    profileListConfirm ?? cachedProfileListConfirmRef.current;

  const postProfileConfirmTimer = useWorkflowStepActorTimer(
    report.workflow_run_uuid,
    WORKFLOW_STEP_STATUS_ENUM.ProfilesManualRate
  );

  const baseItems = stableProfileListConfirm?.items ?? [];
  const displayItems = enableProfiler
    ? profilerOperations.applyOptimisticProfiles(baseItems)
    : baseItems;

  const suggestedProfiles: ItemWithMetaData[] | undefined =
    sortProfilesByPlatform(
      displayItems.filter(
        (item: ItemWithMetaData) =>
          !item.item.in_report && !item.item.is_reviewed
      )
    );

  const handleManualAdd = (inputUrl: string) => {
    if (!report.report_uuid || !inputUrl) return;
    if (urlInExistingProfiles(inputUrl, baseItems)) {
      setErrorMessage("Profile already exists");
      return;
    }

    createProfile({
      profile_url: inputUrl,
      report_uuid: report.report_uuid,
      actor_uuid: HARDCODED_ACTOR_UUID,
    });
  };

  useEffect(() => {
    if (successRating && !enableProfiler) {
      loadProfilesConfirm?.();
      resetCreate();
      setErrorMessage("");
    }
  }, [successRating, resetCreate, loadProfilesConfirm, enableProfiler]);

  useEffect(() => {
    if (successCreateProfile) {
      loadProfilesConfirm?.();
      resetCreate();
      setErrorMessage("");
      if (enableProfiler) {
        handleProfileAdded();
      }
    }
  }, [
    successCreateProfile,
    resetCreate,
    loadProfilesConfirm,
    enableProfiler,
    handleProfileAdded,
  ]);

  useEffect(() => {
    if (successRemoveProfile && !enableProfiler) {
      loadProfilesConfirm?.();
      resetRemove();
      setErrorMessage("");
    }
  }, [loadProfilesConfirm, resetRemove, successRemoveProfile, enableProfiler]);

  const stepDone = useCallback(() => {
    if (isAmendment) {
      navigate("/reports/amendment", { state: { stepDone: true } });
    } else {
      navigate("/reports/next_task", { state: { stepDone: true } });
    }
  }, [isAmendment, navigate]);

  const handleConfirmationModal = useCallback(() => {
    if (
      errorCreateProfile &&
      (
        errorCreateProfile as { response: { data: { detail: string } } }
      )?.response?.data?.detail?.indexOf("not in progress") >= 0
    ) {
      show(
        "",
        "",
        (action) => {
          if (action === "close") {
            stepDone();
          }
        },
        <>
          <span>
            This task has already been completed by another user, your profile
            cannot be saved at this time, please proceed to a new task.
          </span>
          <div className="flex justify-end">
            <Button
              text="OK"
              className="w-50"
              onClick={() => {
                stepDone();
              }}
            ></Button>
          </div>
        </>,
        false
      );
      return;
    }
  }, [errorCreateProfile, show, stepDone]);

  // The mutation's success/error state persists across report prop changes (this component can be
  // reused without unmounting when switching reports). Only honor it when it belongs to the report
  // currently on screen, otherwise a stale result from a previous report would navigate away from,
  // or show a spurious banner on, the new one.
  const isManualRateForCurrentReport =
    !!report.report_uuid &&
    manualRateVariables?.reportUUID === report.report_uuid;

  // The advance race: if a duplicate/late "Next" arrives after the workflow already moved past
  // ProfilesManualRate, the backend reports the step "is not in progress". The report has, in
  // fact, advanced, so this is not a real failure. (Newer backends return success for this case;
  // this detection covers older ones.)
  const manualRateErrorDetail = (
    errorProfileManualRate as
      | { response?: { data?: { detail?: string } } }
      | undefined
  )?.response?.data?.detail;
  const manualRateAlreadyAdvanced =
    isManualRateForCurrentReport &&
    !!manualRateErrorDetail?.includes("not in progress");

  useEffect(() => {
    if (isManualRateForCurrentReport && successProfileManualRateDone) {
      stepDone();
    }
  }, [isManualRateForCurrentReport, successProfileManualRateDone, stepDone]);

  // Proceed to the next task instead of stranding the analyst on a report that is already done
  // with a misleading "Error advancing the report".
  useEffect(() => {
    if (manualRateAlreadyAdvanced) {
      stepDone();
    }
  }, [manualRateAlreadyAdvanced, stepDone]);

  useEffect(() => {
    if (isSuccessProfilesConfirm) {
      resetTimer?.();
    }
  }, [isSuccessProfilesConfirm, resetTimer]);

  useEffect(() => {
    if (errorCreateProfile) {
      setErrorMessage("Error creating profile, please contact support");
      handleConfirmationModal();
      return;
    }
    if (errorRemoveProfile && !enableProfiler) {
      setErrorMessage("Error removing profile, please contact support");
    }
  }, [
    errorCreateProfile,
    errorRemoveProfile,
    handleConfirmationModal,
    enableProfiler,
  ]);

  const handleNext = () => {
    if (!report.report_uuid) return;
    postProfileConfirmTimer(ACTOR_TIMER_STATUS_UI_ENUM.CLOSED);
    profileManualRateDone({
      reportUUID: report.report_uuid,
      workflowRunUUID: report.workflow_run_uuid,
    });
  };

  const handleAddRating = (
    itemUUID: string,
    confirmation?: ProfileAddConfirmation,
    existingContent?: ConfirmContent
  ) => {
    if (!itemUUID) return;
    if (enableProfiler) {
      profilerOperations.enqueueAdd(itemUUID, confirmation, existingContent);
      return;
    }
    createItemRating({
      item_uuid: itemUUID,
      ratings: [{ name: "CONSTELLATION", score_type: "recommendation" }],
    });
  };

  const handleDelete = (itemUUID: string): boolean => {
    if (!itemUUID) {
      return false;
    }
    if (enableProfiler) {
      return profilerOperations.enqueueRemove(itemUUID);
    }
    removeProfile(itemUUID);
    return true;
  };

  const {
    handleDeleteWithUndoToast,
    pendingRemoveToastUUIDs,
    hasPendingRemoveToast,
  } = useDeferredProfileRemove(enableProfiler ? handleDelete : undefined);

  const showGlobalSpinner = enableProfiler
    ? isLoadingProfileManualRateDone ||
      isLoadingCreateProfile ||
      (isLoadingProfilesConfirm && !stableProfileListConfirm)
    : isLoadingProfileManualRateDone ||
      isLoadingItemRating ||
      isLoadingProfilesConfirm ||
      isLoadingCreateProfile ||
      isLoadingRemoveProfile;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <span className="text-3xl font-bold">Profile Confirmation</span>

        <div className="flex items-center">
          <span
            className="leading-4 text-xs font-medium mr-4 text-orange-500 cursor-pointer"
            onClick={handleExit}
          >
            {isAmendment ? "Back to List" : "Exit Profile Confirm"}
          </span>
          {!isReadOnly && canConfirmProfile && (
            <Button
              text={isAmendment ? "Complete Profile Confirm" : "Next"}
              className="w-50"
              disabled={
                (isLoadingProfileManualRateDone &&
                  isManualRateForCurrentReport) ||
                (enableProfiler &&
                  (profilerOperations.hasPendingOperations ||
                    hasPendingRemoveToast))
              }
              onClick={handleNext}
            />
          )}
        </div>
      </div>
      {isManualRateForCurrentReport &&
        errorProfileManualRate &&
        !manualRateAlreadyAdvanced && (
          <ErrorMessageContainer message="Error advancing the report" />
        )}
      {enableProfiler ? (
        stableProfileListConfirm && (
          <ProfilerProfileConfirm
            profileList={sortProfilesByPlatform(displayItems)}
            suggestedProfiles={suggestedProfiles ?? []}
            isReadOnly={isReadOnly}
            handleAddProfile={handleManualAdd}
            handleDeleteProfile={handleDeleteWithUndoToast}
            handleAddRating={handleAddRating}
            pendingItemUUIDs={profilerOperations.pendingItemUUIDs}
            pendingRemoveToastUUIDs={pendingRemoveToastUUIDs}
            recentlyAddedOrder={profilerOperations.recentlyAddedOrder}
            errorMessage={errorMessage}
            hasMoreReview={hasMoreProfilesConfirm}
            loadMoreReview={loadMoreProfilesConfirm}
            isLoadingMoreReview={isLoadingMoreProfilesConfirm}
          />
        )
      ) : (
        <div className="grid grid-cols-3 gap-8">
          <div className="col-span-2">
            {!isLoadingProfilesConfirm && suggestedProfiles && (
              <SuggestedProfiles
                suggestedProfiles={suggestedProfiles}
                isReadOnly={isReadOnly}
                handleAddRating={handleAddRating}
              />
            )}
            {!isLoadingProfilesConfirm && profileListConfirm && (
              <ManualConfirmedProfiles
                profileList={sortProfilesByPlatform(profileListConfirm.items)}
                isReadOnly={isReadOnly}
                handleAddProfile={handleManualAdd}
                handleDeleteProfile={handleDelete}
                errorMessage={errorMessage}
              />
            )}
          </div>
          <div>
            <ConfirmedProfiles
              profileList={sortProfilesByPlatform(
                profileListConfirm?.items ?? []
              )}
              isReadOnly={isReadOnly}
              handleAddProfile={handleManualAdd}
              handleDeleteProfile={handleDelete}
              errorMessage={errorMessage}
              isSidebar={false}
            />
          </div>
        </div>
      )}

      {showGlobalSpinner && <LoadingSpinner />}
      {modal}
    </div>
  );
};

export default ProfileConfirm;
